import * as secp256k1lib from '@exodus/crypto/secp256k1'
import { decodeSeed, encodeAccountID } from '@exodus/ripple-address-codec'
import BN from 'bn.js'

import { ripemd160, sha256, sha512half } from './crypto.js'
import { getEd25519 } from './get-curve.js'

const Secp256k1CurveN = new BN(
  Buffer.from('fffffffffffffffffffffffffffffffebaaedce6af48a03bbfd25e8cd0364141', 'hex')
)

// TODO: clean this up / rewrite from bn.js
const secp256k1 = {
  _deriveScalar(bytes, discrim) {
    for (let i = 0; i <= 0xff_ff_ff_ff; ++i) {
      // We hash the bytes to find a 256 bit number, looping until we are sure it
      // is less than the order of the curve.
      const buffers = [bytes]
      // If the optional discriminator index was passed in, update the hash.
      if (discrim !== undefined) buffers.push(new BN(discrim).toArrayLike(Buffer, 'le', 4))
      buffers.push(new BN(i).toArrayLike(Buffer, 'le', 4))

      const key = new BN(sha512half(...buffers))
      if (key.cmpn(0) > 0 && key.cmp(Secp256k1CurveN) < 0) return key
    }

    throw new Error('impossible unicorn ;)')
  },
  _derivePrivateKey(seed) {
    // This private generator represents the `root` private key, and is what's
    // used by validators for signing when a keypair is generated from a seed.
    const privateGen = secp256k1._deriveScalar(seed)
    const publicGen = secp256k1lib.privateKeyToPublicKey({
      privateKey: privateGen.toArrayLike(Buffer, 'be', 32),
    })
    // A seed can generate many keypairs as a function of the seed and a uint32.
    // Almost everyone just uses the first account, `0`.
    return secp256k1._deriveScalar(publicGen, 0).add(privateGen).mod(Secp256k1CurveN)
  },
  deriveKeypair(entropy) {
    const privateKey = secp256k1._derivePrivateKey(entropy).toArrayLike(Buffer, 'be', 33)
    if (privateKey[0] !== 0) throw new Error('Unexpected') // can't happen as it's mod N
    const publicKey = secp256k1lib.privateKeyToPublicKey({
      privateKey: privateKey.subarray(1),
      format: 'buffer',
    })
    return { privateKey, publicKey }
  },
  sign(message, privateKey) {
    if (privateKey instanceof Uint8Array && privateKey.length === 33 && privateKey[0] === 0) {
      // There sometimes is an extra leading 0 byte
      privateKey = privateKey.subarray(1)
    }

    return secp256k1lib.ecdsaSignHashSync({
      hash: sha512half(message),
      privateKey,
      der: true,
      extraEntropy: null, // TODO: can we flip this to true?
      format: 'buffer',
    })
  },
  /**
   * @param {Buffer} encodedPrivateKey
   */
  getPublicKey(encodedPrivateKey) {},
}

const ed25519 = {
  deriveKeypair(entropy) {
    // expensive to initialize, so we lazy-load it
    const Ed25519 = getEd25519()
    const privateKey = Buffer.from([0xed, ...sha512half(entropy)])
    const publicKey = Buffer.from([0xed, ...Ed25519.keyFromSecret(privateKey.slice(1)).pubBytes()])
    return { privateKey, publicKey }
  },
  sign(message, privateKey) {
    // expensive to initialize, so we lazy-load it
    const Ed25519 = getEd25519()
    return Buffer.from(Ed25519.sign(message, privateKey.slice(1)).toBytes())
  },
}

export function deriveKeypair(seed) {
  const decoded = decodeSeed(seed)
  const fns = decoded.type === 'ed25519' ? ed25519 : secp256k1
  return fns.deriveKeypair(Buffer.from(decoded.bytes))
}

export function sign(message, privateKey) {
  const fns = privateKey.length === 33 && privateKey[0] === 0xed ? ed25519 : secp256k1
  return fns.sign(message, privateKey)
}

/**
 * @param {Buffer} privateKey
 * @returns {Buffer}
 */
export function getPublicKey(privateKey) {
  return secp256k1lib.privateKeyToPublicKey({ privateKey, format: 'buffer' })
}

// encodePublicKey
export function derive(publicKey) {
  return encodeAccountID(ripemd160(sha256(publicKey)))
}

export function encodePublicKey(publicKey) {
  return derive(publicKey)
}

/**
 * @param {Buffer} rawPrivateKey
 * @returns {string}
 */
export function encodePrivateKey(rawPrivateKey) {
  return '00' + rawPrivateKey.toString('hex').toUpperCase()
}

/**
 * @param {string} encodedPrivateKey
 * @returns {string}
 */
export function decodePrivateKey(encodedPrivateKey) {
  return Buffer.from(encodedPrivateKey.slice(2), 'hex')
}

/**
 * @param {string} encodedPrivateKey
 * @returns {Boolean}
 */
export function isValidEncodedPrivateKey(encodedPrivateKey) {
  // eslint-disable-next-line unicorn/prefer-regexp-test, unicorn/better-regex
  return Boolean(encodedPrivateKey.match(/^00[0-9a-fA-f]{64}$/))
  // return Boolean(/^00[\dA-f]{64}$/.test(encodedPrivateKey))
}
